import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useParams } from 'react-router-dom';
import { IProductHolder, PRODUCT_LABELS } from '../../Services/productEntitlements';
import { ProductHoldersTable } from './ProductHoldersTable';
import { useProductHolders } from './useProductHolders';
import { PRODUCTS_WITH_OVERAGE, monthBounds, useProductOverages } from './useProductOverages';

const Summary: React.FC<{ holders: IProductHolder[]; unit: string }> = ({ holders, unit }) => {
	const sum = (f: (h: IProductHolder) => number) => holders.reduce((n, h) => n + f(h), 0);
	// Même règle que la colonne État : une ligne « Illimité » à 0 a bien le droit.
	const noRight = holders.filter((h) => h.blocking && h.quantity === 0 && h.usage > 0).length;
	// Même règle que la colonne État : une ligne « Illimité » n'est jamais au-delà
	// de son droit, sa quantité ne plafonne rien.
	const over = holders.filter((h) => h.blocking && h.quantity > 0 && h.usage > h.quantity).length;
	return (
		<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
			<Chip size="small" label={`${holders.length} compte(s)`} />
			<Chip size="small" label={`${sum((h) => h.quantity)} ${unit} vendu(s)`} />
			<Chip size="small" label={`${sum((h) => h.usage)} ${unit} consommé(s)`} />
			{noRight > 0 && <Chip size="small" color="error" label={`${noRight} consomme(nt) sans droit`} />}
			{over > 0 && <Chip size="small" color="warning" label={`${over} au-delà du droit`} />}
		</Box>
	);
};

/**
 * Qui possède un produit — et qui le consomme sans y avoir droit. Écran de
 * recette de la facturation : la fiche d'un compte dit ce que CE compte a, cet
 * écran dit qui a quoi, ce qui est la seule façon de repérer un droit manquant
 * ou une consommation non facturée sans ouvrir les comptes un par un.
 *
 * Les dépassements (Store, Track — serveurs) se calculent à la demande : un
 * appel par compte, avec le MÊME calcul que la ligne posée sur la facture.
 */
export const ProductHolders: React.FC = () => {
	const { product = '' } = useParams<{ product: string }>();
	const meta = PRODUCT_LABELS[product] || { label: product, unit: 'unités' };
	const { holders, loading, error } = useProductHolders(product);
	const [monthOffset, setMonthOffset] = useState(-1);
	const { amounts, done, running, compute } = useProductOverages(product, holders, monthOffset);
	const withOverage = PRODUCTS_WITH_OVERAGE.includes(product);
	const { from, to } = monthBounds(monthOffset);

	return (
		<Box sx={{ p: 2 }}>
			<Button component={Link} to="/products" startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 1 }}>
				Catalogue produits
			</Button>
			<Card>
				<CardContent>
					<Typography variant="h6">{meta.label}</Typography>
					<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
						Unité : {meta.unit}. Droit = ce qui est vendu (abonnement Stripe ou contrat) ; usage = ce qui est mesuré, avec la
						même règle que le gate de création.
					</Typography>
					{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
					{!loading && !error && <Summary holders={holders} unit={meta.unit} />}
					{withOverage && (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
							<Button size="small" onClick={() => setMonthOffset((m) => m - 1)}>
								‹
							</Button>
							<Typography variant="body2">
								{from} → {to}
							</Typography>
							<Button size="small" disabled={monthOffset >= 0} onClick={() => setMonthOffset((m) => m + 1)}>
								›
							</Button>
							<Button variant="outlined" size="small" disabled={running || holders.length === 0} onClick={compute}>
								{running ? `Calcul… ${done}/${holders.length}` : 'Calculer les dépassements'}
							</Button>
						</Box>
					)}
					{loading && <Typography variant="body2">Chargement…</Typography>}
					{!loading && !error && holders.length === 0 && (
						<Typography variant="body2" color="text.secondary">
							Aucun compte n’a ce produit, et personne ne le consomme.
						</Typography>
					)}
					{!loading && holders.length > 0 && (
						<ProductHoldersTable
							holders={holders}
							overages={withOverage ? amounts : undefined}
							overageLabel={`Dépassement ${from.slice(0, 7)}`}
						/>
					)}
				</CardContent>
			</Card>
		</Box>
	);
};
